import { getStorage } from '@/shared/hooks/use-storage';
import {
  API_BASE,
  TOOLING_METRICS_API_BASE,
  STORE_PROJECT_API_BASE,
  PLANOGRAM_FIXER_API_BASE,
} from '@/shared/constants';
import RoutePaths from '@/routes/route-paths';
import AuthResponsePayload from '@/features/auth/types';
import { Dict } from 'styled-components/dist/types';
import { addHttpBreadcrumb, reportApiError } from '@/shared/services/sentry';

/**
 * @description Used to convert an Obj to query-string
 * @param params
 */
export const convertObjToQueryString = (params: { [key: string]: string | number | string[] | number[] }) =>
  Object.keys(params)
    .filter((key) => params[key] !== undefined)
    .map((key) => (key + '=' + Array.isArray(params[key]) ? (params[key] as (string | number)[]).join() : params[key]))
    .join('&');

export const senseiBackendApiFetch = async (method: string, path: RequestInfo, body?: Dict) => {
  addHttpBreadcrumb(method, String(path));
  return apiFetch(method, API_BASE + path, body);
};

export const toolingMetricsApiFetch = async (method: string, path: RequestInfo, body?: Dict) => {
  addHttpBreadcrumb(method, String(path));
  return apiFetch(method, TOOLING_METRICS_API_BASE + path, body);
};

export const storeProjectApiFetch = async (method: string, path: RequestInfo, body?: Dict) => {
  addHttpBreadcrumb(method, String(path));
  return apiFetch(method, STORE_PROJECT_API_BASE + path, body);
};

export const planogramFixerApiFetch = async (method: string, path: RequestInfo, body?: Dict) => {
  addHttpBreadcrumb(method, String(path));
  return apiFetch(method, PLANOGRAM_FIXER_API_BASE + path, body);
};

/**
 * @description Sensei Backend API fetch for direct file uploads
 * @param method - HTTP method
 * @param path - API path
 * @param file - File to upload
 * @param contentType - Optional content type (defaults to file.type or 'application/octet-stream')
 * @returns {Promise<Response>}
 */
export const senseiBackendApiFetchFile = async (
  method: string,
  path: RequestInfo,
  file: File,
  contentType?: string
): Promise<Response> => {
  const { store } = getStorage();
  const { accessToken } = (await store.getItem<AuthResponsePayload>('auth')) || {};
  return fetch(API_BASE + path, {
    method: method,
    credentials: 'include',
    headers: {
      Authorization: `Bearer ${accessToken}`,
      'Content-Type': contentType || file.type || 'application/octet-stream',
    },
    body: file,
  })
    .then(handleNonAuthorizedAccess)
    .then(handleGenericAPIFetchError);
};

/**
 * @description Store Project API fetch for FormData (file uploads)
 * @param method - HTTP method
 * @param path - API path
 * @param formData - FormData to send
 * @returns {Promise<Response>}
 */
export const storeProjectApiFetchFormData = async (
  method: string,
  path: RequestInfo,
  formData: FormData
): Promise<Response> => {
  const { store } = getStorage();
  const { accessToken } = (await store.getItem<AuthResponsePayload>('auth')) || {};
  return fetch(STORE_PROJECT_API_BASE + path, {
    method: method,
    credentials: 'include',
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
    body: formData,
  })
    .then(handleNonAuthorizedAccess)
    .then(handleGenericAPIFetchError);
};

const apiFetch = async (method: string, path: RequestInfo, body?: Dict) => {
  const { store } = getStorage();
  const { accessToken } = (await store.getItem<AuthResponsePayload>('auth')) || {};
  return fetch(path, {
    method: method,
    credentials: 'include',
    headers: {
      Authorization: `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
    },
    body: body ? JSON.stringify(body) : undefined,
  })
    .then(handleNonAuthorizedAccess)
    .then(handleGenericAPIFetchError);
};

/**
 * @description Used to handle api non-authorized exceptions
 * @param res
 */
const handleNonAuthorizedAccess = (res: Response) => {
  if (res.status === 401) {
    window.location.pathname = RoutePaths.login;
  }
  return res;
};
const handleGenericAPIFetchError = async (res: Response) => {
  if (!res.ok) {
    const rawBody = await res.text().catch(() => '');
    let body: unknown = rawBody;
    try {
      body = rawBody ? JSON.parse(rawBody) : undefined;
    } catch {
      // body is not valid JSON — keep the raw text so the failure is still reported
    }
    reportApiError(res, body);

    const bodyWithStatus =
      body && typeof body === 'object' ? { ...body, status: res.status } : { detail: body, status: res.status };
    throw bodyWithStatus;
  }
  return res;
};
